import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.promise.js";
import "core-js/modules/es.regexp.to-string.js";
import { useCallback, useEffect, useRef, useState } from 'react';
import track from 'utils/track';
import useSearchParameter from 'hooks/useSearchParameter';
import { addMindsetCardAgentIfRequired } from './addMindsetCardAgentIfRequired';
import { peekPendingOpenMessage, clearPendingOpenMessage } from './openMindsetAgentWithMessage';
import { getAgentElement, MINDSET_CARD_ID } from './getAgentElement';
import { renameNextThread } from './threads/renameNextThread';
import { sendMessageToThread } from './threads/sendMessageToThread';
import { updateExtraSituationalAwarenessContext } from './situationalAwareness/useExtraSituationalAwarenessContext';
import { useHideMindsetAgentDock } from './visibility/useHideMindsetAgentDock';
import { useOpenMindsetWithMessageEvent } from './useOpenMindsetWithMessageEvent';
import { syncSituationalAwareness } from './situationalAwareness/syncSituationalAwareness';
// SDK2's <mindset-agent> lacks setSituationalAwareness and renameThread, so both
// calls below no-op there.
function applyBestEffortRefinements(_ref) {
  var threadTitle = _ref.threadTitle,
    coachingContext = _ref.coachingContext;
  updateExtraSituationalAwarenessContext({
    coachingContext: coachingContext || undefined
  });
  var agentEl = getAgentElement(MINDSET_CARD_ID);
  if (coachingContext) {
    syncSituationalAwareness(agentEl);
  }
  renameNextThread(agentEl, threadTitle !== null && threadTitle !== void 0 ? threadTitle : '');
}

// Hides MindsetAgentDock while mounted - only one agent instance active at a time.
export function useMindsetCardAgent() {
  var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref2$forceOnboarding = _ref2.forceOnboardingPlan,
    forceOnboardingPlan = _ref2$forceOnboarding === void 0 ? false : _ref2$forceOnboarding;
  var _useState = useState('loading'),
    _useState2 = _slicedToArray(_useState, 2),
    mindsetLoadingStatus = _useState2[0],
    setMindsetLoadingStatus = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    hasInitialized = _useState4[0],
    setHasInitialized = _useState4[1];
  // Bumped when <mindset-agent> is recreated, so consumers can resync
  // beyond hasInitialized (which only flips once).
  var _useState5 = useState(0),
    _useState6 = _slicedToArray(_useState5, 2),
    renderGeneration = _useState6[0],
    setRenderGeneration = _useState6[1];
  var _useSearchParameter = useSearchParameter('q'),
    _useSearchParameter2 = _slicedToArray(_useSearchParameter, 1),
    q = _useSearchParameter2[0];
  useHideMindsetAgentDock();

  // A ref, not state: state wouldn't update in time to stop a second
  // ensureInitialized() call in the same commit, which would tear down and
  // recreate <mindset-agent>, discarding its session. Mirrors
  // useOpenMindsetAgent.ts's initPromiseRef.
  var initPromiseRef = useRef(null);
  var ensureInitialized = useCallback(function () {
    var search = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
    var silent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    if (hasInitialized) return Promise.resolve(true);
    if (initPromiseRef.current) return initPromiseRef.current;
    var promise = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var _t;
      return _regeneratorRuntime.wrap(function (_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;
            _context.next = 1;
            return addMindsetCardAgentIfRequired({
              render: true,
              search: search,
              forceOnboardingPlan: forceOnboardingPlan,
              silent: silent
            });
          case 1:
            setHasInitialized(true);
            setRenderGeneration(function (generation) {
              return generation + 1;
            });
            return _context.abrupt("return", true);
          case 2:
            _context.prev = 2;
            _t = _context["catch"](0);
            console.error('Mindset:', _t);
            setMindsetLoadingStatus('error');
            return _context.abrupt("return", false);
          case 3:
            _context.prev = 3;
            initPromiseRef.current = null;
            return _context.finish(3);
          case 4:
          case "end":
            return _context.stop();
        }
      }, _callee, null, [[0, 2, 3, 4]]);
    }))();
    initPromiseRef.current = promise;
    return promise;
  }, [hasInitialized, forceOnboardingPlan]);
  useEffect(function () {
    // Cleared synchronously (not after the async init resolves) so
    // useOpenMindsetWithMessageEvent's replay can't also pick this up
    // and re-deliver it.
    var pending = peekPendingOpenMessage();
    if (pending) clearPendingOpenMessage();
    function addMindset() {
      return _addMindset.apply(this, arguments);
    }
    function _addMindset() {
      _addMindset = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
        var initialized;
        return _regeneratorRuntime.wrap(function (_context2) {
          while (1) switch (_context2.prev = _context2.next) {
            case 0:
              _context2.next = 1;
              return ensureInitialized((pending === null || pending === void 0 ? void 0 : pending.message) || (q === null || q === void 0 ? void 0 : q.toString()), pending === null || pending === void 0 ? void 0 : pending.silent);
            case 1:
              initialized = _context2.sent;
              if (initialized && pending !== null && pending !== void 0 && pending.message) {
                applyBestEffortRefinements(pending);
              }
            case 2:
            case "end":
              return _context2.stop();
          }
        }, _callee2);
      }));
      return _addMindset.apply(this, arguments);
    }
    addMindset();
  }, [q, ensureInitialized]);

  // If init hasn't started, this call becomes the init (message as
  // initialQuestion - SDK2's only delivery mechanism). Otherwise waits for
  // the in-flight/existing init and sends via sendMessage instead of
  // re-rendering; safely no-ops on SDK2, which has no sendMessage API.
  var handleOpenWithMessage = useCallback(/*#__PURE__*/function () {
    var _ref4 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee3(message, silent, threadTitle, coachingContext) {
      var alreadyStarting, initialized;
      return _regeneratorRuntime.wrap(function (_context3) {
        while (1) switch (_context3.prev = _context3.next) {
          case 0:
            if (message) {
              _context3.next = 1;
              break;
            }
            return _context3.abrupt("return", true);
          case 1:
            alreadyStarting = hasInitialized || Boolean(initPromiseRef.current);
            _context3.next = 2;
            return ensureInitialized(message, silent);
          case 2:
            initialized = _context3.sent;
            if (initialized) {
              _context3.next = 3;
              break;
            }
            return _context3.abrupt("return", false);
          case 3:
            applyBestEffortRefinements({
              threadTitle: threadTitle,
              coachingContext: coachingContext
            });
            if (alreadyStarting) {
              sendMessageToThread(getAgentElement(MINDSET_CARD_ID), message, silent);
            }
            return _context3.abrupt("return", true);
          case 4:
          case "end":
            return _context3.stop();
        }
      }, _callee3);
    }));
    return function (_x, _x2, _x3, _x4) {
      return _ref4.apply(this, arguments);
    };
  }(), [hasInitialized, ensureInitialized]);
  useOpenMindsetWithMessageEvent(handleOpenWithMessage);
  useEffect(function () {
    // Two entries (open+close), not one updated row, in case the tab
    // closes before unmount fires.
    track('featureUsage', 'mindset');
    var openTime = Date.now();
    return function () {
      var duration = Date.now() - openTime;
      var durationInSeconds = Math.round(duration / 1000);
      track('featureUsage', 'mindset', durationInSeconds);
    };
  }, []);
  return {
    mindsetLoadingStatus: mindsetLoadingStatus,
    setMindsetLoadingStatus: setMindsetLoadingStatus,
    hasInitialized: hasInitialized,
    renderGeneration: renderGeneration
  };
}